import { useState, useEffect, useRef } from 'react';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';

/*
account:
{
  isDark: true,
  isDarkCharts: true
  desktopMenuExpanded: false
  mobileMenuExpanded: false
  networkError: false
  personnelTab: 0
  teamOverviewTab: 0
  theme: {isDark: false, isDarkCharts: true}
}
*/
const useUserConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [account, setAccount] = useState();
  const { getAccessTokenSilently } = useKitbagAuth();
  const isMountedRef = useRef(false);
  const apiUrl = process.env.USER_CONFIG;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(apiUrl, {
          headers: {
            Authorization: `bearer ${token}`,
          },
        });
        const text = await response.text();

        if (!response.ok) {
          throw new Error(`User config request failed: ${response.status}`);
        }

        // The API returns 200 with an empty body for users who have never
        // saved config - treat that as "no config yet" so the account is
        // truthy and consumers (e.g. What's New modal) can proceed.
        // NOTE: only do this for a successful response. On failures we must
        // leave account undefined: save() merges into `account || {}` and
        // PUTs the whole object, so defaulting to {} after a transient
        // 401/500 would let a later save overwrite the user's real config.
        const data = text.trim() === '' ? {} : JSON.parse(text);
        if (isMountedRef.current) {
          setIsLoading(false);
          setAccount(data);
        }
      } catch (err) {
        if (isMountedRef.current) {
          // eslint-disable-next-line no-console
          console.error(err);
          setError(true);
          setIsLoading(false);
        }
      }
    };

    if (!account) {
      fetchData();
    }
  }, [account]);

  /* 
  Gets called with partial inputs ~ needs to be applied to existing account 
    so saving from multiple pages all update their bits without accidentally
    removing other aspects
  */
  const save = async (input) => {
    const curAccount = account || {};
    const updatedAccount = { ...curAccount, ...input };
    const body = JSON.stringify(updatedAccount);

    setIsLoading(true);
    const token = await getAccessTokenSilently();

    await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        Authorization: `bearer ${token}`,
      },
      body,
    }).then(() => {
      setIsLoading(false);
      setAccount(updatedAccount); // update the local value
    });
  };

  /* 
  This method replaces the whole user object with whatever the input is
    Need to be able to delete things ~ down to caller of this function to make sure
    the input is comprehensive
   */
  const saveUnsafe = async (input) => {
    const updatedAccount = { ...input };
    const body = JSON.stringify(updatedAccount);

    setIsLoading(true);
    const token = await getAccessTokenSilently();
    await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        Authorization: `bearer ${token}`,
      },
      body,
    }).then(() => {
      setIsLoading(false);
      setAccount(updatedAccount); // update the local value
    });
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { isLoading, error, account, save, saveUnsafe };
};

export default useUserConfig;
